import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Download, FilePlus2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { EmptyState, ErrorStrip, LoadingRows } from '@/shared/ui/states'
import { useDebounced } from '@/shared/hooks/useDebounced'
import {
  exportIncidents, listIncidents, type IncidentSummary, type RegisterFilter,
} from './api'
import { useVocabulary } from './useIncident'
import { IncidentDrawer } from './IncidentDrawer'
import { SeverityChip, StatusChip, VerificationChip, ageOf, label, options } from './vocab'

/**
 * The register — every incident in the caller's scope.
 *
 * ## Scope is the server's job, not a filter here
 *
 * A ward-scoped caller sees their ward because `unit_path <@ :callerPath` says so, and an
 * out-of-scope id comes back 404 rather than 403 — a 403 would confirm the id exists.
 * There is no "national/county/ward" control on this page for that reason: the caller's
 * scope is not something the client gets to choose.
 *
 * ## Every mutating control is offered
 *
 * Not hidden on a guess about the caller's authorities. A 403 carrying a sentence that
 * names the missing permission teaches; a missing button does not, and a reporter who
 * cannot see the Report action concludes the desk does not accept reports.
 *
 * ## Every id column renders a NAME
 *
 * `unitName` and `assignedToName` are resolved server-side in one batched lookup. Resolving
 * them here would be one request per row.
 */
export function RegisterPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { vocab } = useVocabulary()

  const [search, setSearch] = useState(params.get('q') ?? '')
  const debouncedSearch = useDebounced(search, 300)
  const [category, setCategory] = useState<string[]>(listParam(params, 'category'))
  const [severity, setSeverity] = useState<string[]>(listParam(params, 'severity'))
  const [status, setStatus] = useState<string[]>(listParam(params, 'status'))
  const [verification, setVerification] = useState<string[]>(listParam(params, 'verification'))
  const [mine, setMine] = useState(params.get('mine') === 'true')

  const [rows, setRows] = useState<IncidentSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const openId = params.get('open')

  /*
     The server takes one code per axis, not a list. The multi-selects are kept because
     they are the shell's filter idiom, and the FIRST selection is what is sent — with the
     extras filtered locally so the count on screen matches the count in the table.

     Sending only the first and silently dropping the rest would be the drift this
     codebase's mock-route check exists to catch, so it is stated rather than hidden: the
     note under the bar says so where more than one is chosen.
  */
  const filter: RegisterFilter = useMemo(() => ({
    category: category[0] ?? null,
    severity: severity[0] ?? null,
    status: status[0] ?? null,
    verification: verification[0] ?? null,
    mine,
    search: debouncedSearch || null,
    size: 200,
  }), [category, severity, status, verification, mine, debouncedSearch])

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await listIncidents(filter))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [filter])

  // `filter` is listed, and it is derived from every control above. A `[]` here would bind
  // the loader to the empty first-render filter and the page would never respond to a
  // filter change — the exact defect four station-mates pages shipped with.
  useEffect(() => { void load() }, [load])

  // Keep the URL in step, so a filtered register is a shareable link.
  useEffect(() => {
    const next = new URLSearchParams(params)
    setListParam(next, 'category', category)
    setListParam(next, 'severity', severity)
    setListParam(next, 'status', status)
    setListParam(next, 'verification', verification)
    if (debouncedSearch) next.set('q', debouncedSearch); else next.delete('q')
    if (mine) next.set('mine', 'true'); else next.delete('mine')
    setParams(next, { replace: true })
    // `params`/`setParams` deliberately omitted: including them re-runs this on the write
    // it just made, which is an infinite loop rather than a subtle one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, severity, status, verification, mine, debouncedSearch])

  const visible = useMemo(() => narrowLocally(rows ?? [], {
    category, severity, status, verification,
  }), [rows, category, severity, status, verification])

  const drafts = visible.filter((r) => r.statusCode === 'DRAFT')
  const activeCount = [category, severity, status, verification]
    .filter((a) => a.length > 0).length + (mine ? 1 : 0) + (debouncedSearch ? 1 : 0)
  const multiChosen = [category, severity, status, verification].some((a) => a.length > 1)

  return (
    <div className="space-y-3">
      <FilterBar
        activeCount={activeCount}
        right={(
          <>
            <button type="button"
              onClick={() => navigate('/incidents/report')}
              className="erp-btn-primary h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm">
              <FilePlus2 className="h-3.5 w-3.5" /> Report an incident
            </button>
            <button type="button" disabled={exporting}
              onClick={async () => {
                setExporting(true)
                try { await exportIncidents(filter) }
                catch (err) { setError(explain(err)) }
                finally { setExporting(false) }
              }}
              className="erp-btn h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm">
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Export CSV
            </button>
          </>
        )}
      >
        <SearchInput value={search} onChange={setSearch} placeholder="Ref, title, description…" />
        <MultiSelect label="Category" options={options(vocab?.categories)}
          selected={category} onChange={setCategory} />
        <MultiSelect label="Severity" options={options(vocab?.severities)}
          selected={severity} onChange={setSeverity} />
        <MultiSelect label="Status" options={options(vocab?.statuses)}
          selected={status} onChange={setStatus} />
        <MultiSelect label="Verification" options={options(vocab?.verifications)}
          selected={verification} onChange={setVerification} />
        <label className="h-9 px-3 inline-flex items-center gap-2 border border-input rounded-md bg-background text-erp-sm">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          Mine only
        </label>
      </FilterBar>

      {multiChosen && (
        <p className="text-erp-xs text-muted-foreground px-1">
          The server filters one code per axis. The extra selections are applied here, over
          the rows it returned — so a very long register may not include every match.
        </p>
      )}

      <ErrorStrip message={error} className="rounded-md border" />

      {drafts.length > 0 && (
        <div className="erp-card border-l-[3px] border-l-chocolate bg-chocolate-soft/30 px-3 py-2.5">
          <div className="text-erp-sm font-medium mb-1.5">
            {drafts.length} unfinished {drafts.length === 1 ? 'report' : 'reports'}
            <span className="font-normal text-muted-foreground">
              {' '}— saved, yours alone, and not visible to anyone else until submitted.
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {drafts.map((d) => (
              <button key={d.id} type="button"
                onClick={() => navigate(`/incidents/report?draft=${d.id}`)}
                className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5 max-w-72">
                <FilePlus2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{d.title || d.ref}</span>
                <span className="text-chocolate shrink-0">Resume</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <ErpTable head={(
        <>
          <Th>Ref</Th>
          <Th>Title</Th>
          <Th>Category</Th>
          <Th>Severity</Th>
          <Th>Status</Th>
          <Th>Verification</Th>
          <Th>Unit</Th>
          <Th>Occurred</Th>
          <Th className="text-right">Impact</Th>
          <Th className="text-right">Evidence</Th>
          <Th className="text-right">Age</Th>
        </>
      )}>
        {rows === null ? <LoadingRows colSpan={11} label="Loading the register…" /> : (
          visible.length === 0 ? (
            <tr>
              <td colSpan={11}>
                <EmptyState
                  title={activeCount > 0 ? 'Nothing matches these filters' : 'No incidents in your scope yet'}
                  hint={activeCount > 0
                    ? 'Clear a filter, or widen the date range. The register only ever shows what your scope covers.'
                    : 'When somebody files a report inside the area you cover, it appears here.'}
                />
              </td>
            </tr>
          ) : visible.map((r) => (
            <tr key={r.id}
              /*
                 A DRAFT reopens the WIZARD; everything else opens the drawer.

                 A draft has nothing to triage — no timeline worth reading, no evidence to
                 weigh, no action bar that would not be refused — so sending it to the
                 read-only drawer left the author looking at their own unfinished report
                 with no way back into the form. Resuming is the only thing anybody wants
                 to do with a draft.
              */
              onClick={() => (r.statusCode === 'DRAFT'
                ? navigate(`/incidents/report?draft=${r.id}`)
                : setParams((p) => { p.set('open', r.id); return p }, { replace: true }))}
              className={cn(
                'cursor-pointer hover:bg-muted/40',
                r.statusCode === 'DRAFT' && 'bg-amber-50/40 dark:bg-amber-950/10',
              )}>
              <Td className="font-mono text-erp-xs whitespace-nowrap">
                {r.ref}
                {r.statusCode === 'DRAFT' && (
                  <span className="ml-2 font-sans not-italic text-chocolate">Resume →</span>
                )}
              </Td>
              <Td className="max-w-72">
                <div className="truncate" title={r.title}>{r.title}</div>
                {r.subcategory && (
                  <div className="text-erp-xs text-muted-foreground truncate">{r.subcategory}</div>
                )}
              </Td>
              <Td>{label(r.categoryCode)}</Td>
              <Td><SeverityChip code={r.severityCode} /></Td>
              <Td><StatusChip code={r.statusCode} /></Td>
              <Td><VerificationChip code={r.verificationCode} /></Td>
              <Td className="max-w-40">
                <span className="truncate block" title={r.unitName ?? undefined}>
                  {r.unitName ?? (r.hasLocation ? 'coordinate only' : '—')}
                </span>
              </Td>
              <Td className="whitespace-nowrap text-erp-xs">
                {r.occurredAt ? new Date(r.occurredAt).toLocaleDateString() : '—'}
              </Td>
              <Td className="text-right tabular-nums whitespace-nowrap">
                {impactOf(r)}
              </Td>
              <Td className="text-right tabular-nums">{r.evidenceCount}</Td>
              <Td className="text-right tabular-nums">{ageOf(r.reportedAt)}</Td>
            </tr>
          ))
        )}
      </ErpTable>

      <IncidentDrawer
        id={openId}
        onClose={() => setParams((p) => { p.delete('open'); return p }, { replace: true })}
        onChanged={load}
      />
    </div>
  )
}

/** Deaths and injuries, as one cell. A dash where nobody has counted — not a zero. */
function impactOf(r: IncidentSummary): string {
  const parts: string[] = []
  if (r.deathCount != null) parts.push(`${r.deathCount}†`)
  if (r.injuryCount != null) parts.push(`${r.injuryCount}+`)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

/** The second and later selections on each axis, which the server does not take. */
function narrowLocally(rows: IncidentSummary[], sel: {
  category: string[]; severity: string[]; status: string[]; verification: string[]
}): IncidentSummary[] {
  return rows.filter((r) =>
    (sel.category.length === 0 || sel.category.includes(r.categoryCode))
    && (sel.severity.length === 0 || sel.severity.includes(r.severityCode))
    && (sel.status.length === 0 || sel.status.includes(r.statusCode))
    && (sel.verification.length === 0 || sel.verification.includes(r.verificationCode)))
}

function listParam(params: URLSearchParams, key: string): string[] {
  return params.getAll(key).filter(Boolean)
}

function setListParam(params: URLSearchParams, key: string, values: string[]) {
  params.delete(key)
  for (const v of values) params.append(key, v)
}
